import { Box, Stack, Typography, useMediaQuery } from "@mui/material";
import { ChatSurveyElement } from "@pulse/pulse-rpcs/lib/survey-respondent/chatSurveyElement";
import { ChatSurveyNextAction } from "@pulse/pulse-rpcs/lib/survey-respondent/chatSurveyNextAction";
import { ChatQuestionState } from "@pulse/pulse-rpcs/lib/survey-respondent/chatQuestionState";
import { QuestionType } from "@pulse/pulse-rpcs/lib/types/questionType";
import { SurveySettingsEnums } from "@pulse/pulse-rpcs/lib/types/surveySettings";
import {
  DESKTOP_SCREEN_CONDITION,
  EMPTY_JSON,
  RICH_TEXT_DEFAULT_FONT_COLOR,
  RICH_TEXT_MARKDOWN_PROPS,
  RTERegularTextType,
  richTextTypography,
} from "@pulse/shared-components";
import {
  Button,
  RichTextAttachmentConfig,
  RichTextTypography,
  useFoundationColorTokens,
  useSpacing,
  useTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import { RichTextEditor } from "@surya-digital/leo-reactjs-remirror";
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { ChatItemAction, ChatItemContainer } from "./ChatItemContainer";
import {
  ChatMultiSelectAnswerList,
  ChatMultiSelectQuestionBody,
} from "./ChatMultiSelectQuestionBody";
import {
  ChatSingleSelectAnswerText,
  ChatSingleSelectQuestionBody,
} from "./ChatSingleSelectQuestionBody";
import {
  ChatOpenEndedAnswer,
  ChatOpenEndedQuestionBody,
} from "./ChatOpenEndedQuestionBody";
import {
  ChatRankingAnswerList,
  ChatRankingQuestionBody,
} from "./ChatRankingQuestionBody";
import {
  ChatGroupQuestionActive,
  ChatGroupQuestionCompleted,
} from "./ChatGroupQuestion";
import {
  ChatGridQuestionActive,
  ChatGridQuestionCompleted,
} from "./ChatGridQuestion";
import { CHAT_TIMELINE_VERTICAL_GUTTER } from "./ChatSurveyLayout";
import {
  chatScrollBehavior,
  followChatToBottom,
  revealEditedChatItem,
  scrollChatToBottom,
} from "./ChatScroll";

interface ChatSurveyTimelineProps {
  elements: readonly ChatSurveyElement.ChatSurveyElementDTO[];
  timelineRevision: number;
  nextAction: ChatSurveyNextAction.ChatSurveyNextActionDTO | null;
  getElementKey: (element: ChatSurveyElement.ChatSurveyElementDTO) => string;
  startButtonText: string;
  nextButtonText: string;
  previousButtonText: string;
  groupNextQuestionButtonText: string;
  groupSendResponseButtonText: string;
  areActionsDisabled: boolean;
  isEditingLayoutActive: boolean;
  fontFamily: string;
  fontScale: SurveySettingsEnums.FontSizeScale.FontSizeScale;
  attachmentConfig: RichTextAttachmentConfig;
  dropdownPlaceholder: string;
  multiSelectMaxSelectionText: string;
  rankingAllOptionsMandatoryText: string;
  resetAnswerButtonText: string;
  editAnswerButtonText: string;
  editAnswersButtonText: string;
  cancelEditAnswerButtonText: string;
  submitRetryPrompt?: ChatSubmitRetryPrompt;
  openEndedShowMoreText: string;
  openEndedShowLessText: string;
  skippedAnswerText: string;
  onStartSurvey: () => void;
  onSaveAnswer: (questionId: string, answerJson: string) => void;
  onEditStateChange: (isEditing: boolean) => void;
}

interface ChatSubmitRetryPrompt {
  description: string;
  buttonText: string;
  disabled: boolean;
  onClick: () => void;
}

interface ChatMessageRichTextProps {
  name: string;
  text: string;
  typography: RichTextTypography;
  attachmentConfig: RichTextAttachmentConfig;
}

interface ChatQuestionPresentation {
  body: ReactNode;
  completedAnswer: ReactNode;
  primaryAction?: ChatItemAction;
  secondaryAction?: ChatItemAction;
  bodyScrollMode?: "always";
}

const ChatMessageRichText = ({
  name,
  text,
  typography,
  attachmentConfig,
}: ChatMessageRichTextProps): React.ReactElement => (
  <RichTextEditor
    name={name}
    initialValue={text}
    typography={typography}
    mode="preview"
    supports={RICH_TEXT_MARKDOWN_PROPS}
    borderOnPreview={false}
    attachmentConfig={attachmentConfig}
  />
);

export const ChatSurveyTimeline = ({
  elements,
  timelineRevision,
  nextAction,
  getElementKey,
  startButtonText,
  nextButtonText,
  previousButtonText,
  groupNextQuestionButtonText,
  groupSendResponseButtonText,
  areActionsDisabled,
  isEditingLayoutActive,
  fontFamily,
  fontScale,
  attachmentConfig,
  dropdownPlaceholder,
  multiSelectMaxSelectionText,
  rankingAllOptionsMandatoryText,
  resetAnswerButtonText,
  editAnswerButtonText,
  editAnswersButtonText,
  cancelEditAnswerButtonText,
  submitRetryPrompt,
  openEndedShowMoreText,
  openEndedShowLessText,
  skippedAnswerText,
  onStartSurvey,
  onSaveAnswer,
  onEditStateChange,
}: ChatSurveyTimelineProps): React.ReactElement => {
  const spacing = useSpacing();
  const tokens = useFoundationColorTokens();
  const typography = useTypography();
  const { t } = useTranslation();
  const endOfTimelineRef = useRef<HTMLDivElement>(null);
  const editingQuestionRef = useRef<HTMLLIElement>(null);
  const timelineContentRef = useRef<HTMLOListElement>(null);
  const previousElementKeys = useRef<string[] | null>(null);
  const wasSubmitRetryPromptVisible = useRef(false);
  const [pendingAnswers, setPendingAnswers] = useState<
    Record<string, string | null>
  >({});
  const [pendingAnswerValidity, setPendingAnswerValidity] = useState<
    Record<string, boolean>
  >({});
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(
    null,
  );
  const [editingGroupChildCode, setEditingGroupChildCode] = useState<
    string | null
  >(null);
  const [editingGridRowCode, setEditingGridRowCode] = useState<string | null>(
    null,
  );
  const [editingGroupGridRowCode, setEditingGroupGridRowCode] = useState<
    string | null
  >(null);
  const savedOuterScrollTop = useRef<number | null>(null);
  const editSaveRevision = useRef<number | null>(null);
  const pendingPostEditScroll = useRef(false);
  const postEditScrollStartTop = useRef<number | null>(null);
  const rankingResetActions = useRef<Record<string, (() => void) | null>>({});
  const isBigScreen = useMediaQuery(DESKTOP_SCREEN_CONDITION);
  const isSubmitRetryPromptVisible = submitRetryPrompt !== undefined;
  const regularTypography = richTextTypography(
    fontFamily,
    fontScale,
    RICH_TEXT_DEFAULT_FONT_COLOR,
    isBigScreen,
    RTERegularTextType.REGULAR_CUSTOMER_FACING,
  );
  useLayoutEffect(() => {
    const currentKeys = elements.map(getElementKey);
    const previousKeys = previousElementKeys.current;
    const hasNewElement =
      previousKeys !== null &&
      currentKeys.some((key) => !previousKeys.includes(key));
    const hasNewRetryPrompt =
      isSubmitRetryPromptVisible && !wasSubmitRetryPromptVisible.current;
    const isEditingTransition =
      editingQuestionId !== null ||
      editSaveRevision.current !== null ||
      pendingPostEditScroll.current;
    let stopFollowing: (() => void) | undefined;

    if (previousKeys === null || previousKeys.length === 0) {
      scrollChatToBottom(endOfTimelineRef.current, "auto");
    } else if (!isEditingTransition && hasNewRetryPrompt) {
      scrollChatToBottom(endOfTimelineRef.current, "auto");
    } else if (!isEditingTransition && hasNewElement) {
      stopFollowing = followChatToBottom(
        endOfTimelineRef.current,
        timelineContentRef.current,
        chatScrollBehavior(),
      );
    }

    previousElementKeys.current = currentKeys;
    wasSubmitRetryPromptVisible.current = isSubmitRetryPromptVisible;
    return stopFollowing;
  }, [
    elements,
    getElementKey,
    isSubmitRetryPromptVisible,
    timelineRevision,
    editingQuestionId,
  ]);

  useLayoutEffect(() => {
    onEditStateChange(editingQuestionId !== null);
    return (): void => onEditStateChange(false);
  }, [editingQuestionId, onEditStateChange]);

  useLayoutEffect(() => {
    if (!isEditingLayoutActive || editingQuestionId === null) {
      return undefined;
    }
    const item = editingQuestionRef.current;
    if (!item) return undefined;

    revealEditedChatItem(item);
    const frame = requestAnimationFrame(() => revealEditedChatItem(item));
    const observer = new ResizeObserver(() => revealEditedChatItem(item));
    observer.observe(item);
    const bodyContent = item.querySelector<HTMLElement>(
      "[data-chat-item-body] > div",
    );
    if (bodyContent) observer.observe(bodyContent);
    const stopObserving = (): void => observer.disconnect();
    const timeout = window.setTimeout(stopObserving, 500);
    item.addEventListener("pointerdown", stopObserving, { once: true });
    item.addEventListener("keydown", stopObserving, { once: true });

    return (): void => {
      cancelAnimationFrame(frame);
      window.clearTimeout(timeout);
      stopObserving();
      item.removeEventListener("pointerdown", stopObserving);
      item.removeEventListener("keydown", stopObserving);
    };
  }, [editingQuestionId, isEditingLayoutActive]);

  useLayoutEffect(() => {
    if (!pendingPostEditScroll.current) {
      return undefined;
    }

    const container = endOfTimelineRef.current?.closest<HTMLElement>(
      "[data-chat-scroll-container]",
    );
    if (
      editingQuestionId === null &&
      container &&
      postEditScrollStartTop.current !== null
    ) {
      container.scrollTo({
        top: postEditScrollStartTop.current,
        behavior: "auto",
      });
    }
    if (isEditingLayoutActive || editingQuestionId !== null) return undefined;

    pendingPostEditScroll.current = false;
    postEditScrollStartTop.current = null;
    let stopFollowing: (() => void) | undefined;
    let secondFrame = 0;
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => {
        stopFollowing = followChatToBottom(
          endOfTimelineRef.current,
          timelineContentRef.current,
          chatScrollBehavior(),
        );
      });
    });
    return (): void => {
      cancelAnimationFrame(firstFrame);
      cancelAnimationFrame(secondFrame);
      stopFollowing?.();
    };
  }, [editingQuestionId, isEditingLayoutActive, timelineRevision]);

  useLayoutEffect(() => {
    if (
      editSaveRevision.current !== null &&
      timelineRevision > editSaveRevision.current
    ) {
      editSaveRevision.current = null;
      pendingPostEditScroll.current = true;
      postEditScrollStartTop.current =
        endOfTimelineRef.current?.closest<HTMLElement>(
          "[data-chat-scroll-container]",
        )?.scrollTop ?? null;
      setEditingQuestionId(null);
      setEditingGroupChildCode(null);
      setEditingGridRowCode(null);
      setEditingGroupGridRowCode(null);
    }
  }, [timelineRevision]);

  useEffect(() => {
    const activeQuestionIds = new Set(
      elements
        .filter(
          (element): element is ChatSurveyElement.QuestionDTO =>
            element.case === "QUESTION" &&
            (element as ChatSurveyElement.QuestionDTO).questionState.case ===
              "ACTIVE",
        )
        .map((question) => question.respondentQuestionDetails.questionId),
    );
    if (editingQuestionId) activeQuestionIds.add(editingQuestionId);
    setPendingAnswers((answers) => {
      const retained = Object.fromEntries(
        Object.entries(answers).filter(([id]) => activeQuestionIds.has(id)),
      );
      return Object.keys(retained).length === Object.keys(answers).length
        ? answers
        : retained;
    });
    setPendingAnswerValidity((validity) => {
      const retained = Object.fromEntries(
        Object.entries(validity).filter(([id]) => activeQuestionIds.has(id)),
      );
      return Object.keys(retained).length === Object.keys(validity).length
        ? validity
        : retained;
    });
  }, [elements, editingQuestionId, timelineRevision]);

  const beginEditing = (
    questionId: string,
    groupChildCode: string | null = null,
    gridRowCode: string | null = null,
    groupGridRowCode: string | null = null,
  ): void => {
    pendingPostEditScroll.current = false;
    postEditScrollStartTop.current = null;
    const outerScrollContainer = endOfTimelineRef.current?.closest<HTMLElement>(
      "[data-chat-scroll-container]",
    );
    if (outerScrollContainer) {
      // An earlier smooth scroll can otherwise continue toward the tail in edit mode.
      outerScrollContainer.scrollTo({
        top: outerScrollContainer.scrollTop,
        behavior: "auto",
      });
    }
    savedOuterScrollTop.current = outerScrollContainer?.scrollTop ?? 0;
    setEditingGroupChildCode(groupChildCode);
    setEditingGridRowCode(gridRowCode);
    setEditingGroupGridRowCode(groupGridRowCode);
    setEditingQuestionId(questionId);
  };

  const cancelEditing = (): void => {
    pendingPostEditScroll.current = false;
    postEditScrollStartTop.current = null;
    setEditingQuestionId(null);
    setEditingGroupChildCode(null);
    setEditingGridRowCode(null);
    setEditingGroupGridRowCode(null);
    requestAnimationFrame(() => {
      const outerScrollContainer =
        endOfTimelineRef.current?.closest<HTMLElement>(
          "[data-chat-scroll-container]",
        );
      if (outerScrollContainer && savedOuterScrollTop.current !== null) {
        outerScrollContainer.scrollTop = savedOuterScrollTop.current;
      }
      savedOuterScrollTop.current = null;
    });
  };

  const submitAnswer = (questionId: string, answerJson: string): void => {
    if (editingQuestionId === questionId) {
      editSaveRevision.current = timelineRevision;
    }
    onSaveAnswer(questionId, answerJson);
  };

  const setPendingAnswer = useCallback(
    (questionId: string, answerJson: string | null, isValid = true): void => {
      setPendingAnswers((answers) => {
        if (answers[questionId] === answerJson) return answers;
        return { ...answers, [questionId]: answerJson };
      });
      setPendingAnswerValidity((validity) =>
        validity[questionId] === isValid
          ? validity
          : { ...validity, [questionId]: isValid },
      );
    },
    [],
  );

  const getQuestionPresentation = (
    question: ChatSurveyElement.QuestionDTO,
    questionType: QuestionType.QuestionType,
    isCurrentQuestion: boolean,
    isEditing: boolean,
  ): ChatQuestionPresentation => {
    switch (questionType) {
      case QuestionType.QuestionType.MESSAGE: {
        return {
          body: null,
          completedAnswer: null,
          primaryAction: isCurrentQuestion
            ? {
                label: nextButtonText,
                name: "sendChatSurveyResponse",
                onClick: (): void => {
                  submitAnswer(
                    question.respondentQuestionDetails.questionId,
                    EMPTY_JSON,
                  );
                },
                disabled: areActionsDisabled,
              }
            : undefined,
        };
      }
      case QuestionType.QuestionType.RANKING: {
        const questionId = question.respondentQuestionDetails.questionId;
        const draftAnswerJson =
          isEditing && question.questionState.case === "COMPLETED"
            ? (question.questionState as ChatQuestionState.CompletedDTO)
                .answerJson
            : question.questionState.case === "ACTIVE"
              ? ((question.questionState as ChatQuestionState.ActiveDTO)
                  .draftAnswerJson ?? undefined)
              : undefined;
        const answerJson = pendingAnswers[questionId] ?? null;
        return {
          body: (
            <ChatRankingQuestionBody
              key={`${questionId}:${timelineRevision}`}
              questionDetailsJson={
                question.respondentQuestionDetails.questionDetailsJSON
              }
              draftAnswerJson={draftAnswerJson}
              fontFamily={fontFamily}
              fontScale={fontScale}
              areAllOptionsMandatoryText={rankingAllOptionsMandatoryText}
              maxSelectionText={multiSelectMaxSelectionText}
              isMandatory={question.respondentQuestionDetails.isMandatory}
              onAnswerChange={(nextAnswerJson, isValid): void => {
                setPendingAnswer(questionId, nextAnswerJson, isValid);
              }}
              onResetReady={(reset): void => {
                if (reset) {
                  rankingResetActions.current[questionId] = reset;
                } else {
                  delete rankingResetActions.current[questionId];
                }
              }}
            />
          ),
          completedAnswer: ((): React.ReactElement | null => {
            if (question.questionState.case !== "COMPLETED") return null;
            const completedAnswerJson = (
              question.questionState as ChatQuestionState.CompletedDTO
            ).answerJson;
            return (
              <ChatItemContainer
                alignment="outgoing"
                ariaLabel={t("surveys.chatInterface.surveyAnswerAriaLabel")}
              >
                {completedAnswerJson === EMPTY_JSON ? (
                  <Typography sx={typography.b1}>
                    {skippedAnswerText}
                  </Typography>
                ) : (
                  <ChatRankingAnswerList
                    questionDetailsJson={
                      question.respondentQuestionDetails.questionDetailsJSON
                    }
                    answerJson={completedAnswerJson}
                  />
                )}
              </ChatItemContainer>
            );
          })(),
          primaryAction: isCurrentQuestion
            ? {
                label: nextButtonText,
                name: "sendChatSurveyResponse",
                onClick: (): void => {
                  submitAnswer(questionId, answerJson ?? EMPTY_JSON);
                },
                disabled:
                  areActionsDisabled ||
                  pendingAnswerValidity[questionId] === false ||
                  (question.respondentQuestionDetails.isMandatory &&
                    answerJson === null),
              }
            : undefined,
          secondaryAction: isCurrentQuestion
            ? {
                label: resetAnswerButtonText,
                name: "resetRanking",
                onClick: (): void => {
                  rankingResetActions.current[questionId]?.();
                },
                disabled: areActionsDisabled || answerJson === null,
              }
            : undefined,
        };
      }
      case QuestionType.QuestionType.GROUP: {
        const questionId = question.respondentQuestionDetails.questionId;
        const draftAnswerJson =
          isEditing && question.questionState.case === "COMPLETED"
            ? (question.questionState as ChatQuestionState.CompletedDTO)
                .answerJson
            : question.questionState.case === "ACTIVE"
              ? ((question.questionState as ChatQuestionState.ActiveDTO)
                  .draftAnswerJson ?? undefined)
              : undefined;
        const completedAnswerJson =
          question.questionState.case === "COMPLETED"
            ? (question.questionState as ChatQuestionState.CompletedDTO)
                .answerJson
            : null;
        const groupLabels = {
          cancelEditAnswerText: cancelEditAnswerButtonText,
          nextQuestionText: groupNextQuestionButtonText,
          sendResponseText: groupSendResponseButtonText,
          previousText: previousButtonText,
          skippedAnswerText,
          showMoreText: openEndedShowMoreText,
          showLessText: openEndedShowLessText,
          dropdownPlaceholder,
          multiSelectMaxSelectionText,
          rankingAllOptionsMandatoryText,
          editAnswerText: editAnswerButtonText,
        };
        return {
          body: (
            <ChatGroupQuestionActive
              key={`${questionId}:${timelineRevision}:${editingGroupChildCode ?? "active"}:${editingGroupGridRowCode ?? "first"}`}
              questionDetailsJson={
                question.respondentQuestionDetails.questionDetailsJSON
              }
              completedAnswerJson={draftAnswerJson}
              parentIsMandatory={question.respondentQuestionDetails.isMandatory}
              initialChildQuestionCode={editingGroupChildCode ?? undefined}
              initialGridRowCode={editingGroupGridRowCode ?? undefined}
              labels={groupLabels}
              fontFamily={fontFamily}
              fontScale={fontScale}
              areActionsDisabled={areActionsDisabled}
              isEditing={isEditing}
              onCancelEdit={cancelEditing}
              onSubmit={(answerJson): void => {
                submitAnswer(questionId, answerJson);
              }}
            />
          ),
          completedAnswer:
            completedAnswerJson === null ? null : (
              <ChatGroupQuestionCompleted
                questionDetailsJson={
                  question.respondentQuestionDetails.questionDetailsJSON
                }
                completedAnswerJson={completedAnswerJson}
                labels={groupLabels}
                areActionsDisabled={areActionsDisabled}
                showEditActions={nextAction?.case !== "SURVEY_COMPLETED"}
                onEditChild={(childQuestionCode, gridRowCode): void => {
                  beginEditing(
                    questionId,
                    childQuestionCode,
                    null,
                    gridRowCode,
                  );
                }}
              />
            ),
        };
      }
      case QuestionType.QuestionType.GRID: {
        const questionId = question.respondentQuestionDetails.questionId;
        const draftAnswerJson =
          isEditing && question.questionState.case === "COMPLETED"
            ? (question.questionState as ChatQuestionState.CompletedDTO)
                .answerJson
            : question.questionState.case === "ACTIVE"
              ? ((question.questionState as ChatQuestionState.ActiveDTO)
                  .draftAnswerJson ?? undefined)
              : undefined;
        const completedAnswerJson =
          question.questionState.case === "COMPLETED"
            ? (question.questionState as ChatQuestionState.CompletedDTO)
                .answerJson
            : null;
        const gridLabels = {
          cancelEditAnswerText: cancelEditAnswerButtonText,
          nextQuestionText: groupNextQuestionButtonText,
          sendResponseText: groupSendResponseButtonText,
          previousText: previousButtonText,
          skippedAnswerText,
          showMoreText: openEndedShowMoreText,
          showLessText: openEndedShowLessText,
          editAnswersText: editAnswersButtonText,
          dropdownPlaceholder,
          multiSelectMaxSelectionText,
        };
        return {
          body: (
            <ChatGridQuestionActive
              key={`${questionId}:${timelineRevision}:${editingGridRowCode ?? "active"}`}
              questionDetailsJson={
                question.respondentQuestionDetails.questionDetailsJSON
              }
              completedAnswerJson={draftAnswerJson}
              isMandatory={question.respondentQuestionDetails.isMandatory}
              initialRowCode={editingGridRowCode ?? undefined}
              labels={gridLabels}
              fontFamily={fontFamily}
              fontScale={fontScale}
              areActionsDisabled={areActionsDisabled}
              isEditing={isEditing}
              onCancelEdit={cancelEditing}
              onSubmit={(answerJson): void =>
                submitAnswer(questionId, answerJson)
              }
            />
          ),
          completedAnswer:
            completedAnswerJson === null ? null : (
              <ChatGridQuestionCompleted
                questionDetailsJson={
                  question.respondentQuestionDetails.questionDetailsJSON
                }
                completedAnswerJson={completedAnswerJson}
                labels={gridLabels}
                areActionsDisabled={areActionsDisabled}
                showEditActions={nextAction?.case !== "SURVEY_COMPLETED"}
                onEditRow={(rowCode): void => {
                  beginEditing(questionId, null, rowCode);
                }}
              />
            ),
        };
      }
      case QuestionType.QuestionType.OPEN_ENDED: {
        const questionId = question.respondentQuestionDetails.questionId;
        const draftAnswerJson =
          isEditing && question.questionState.case === "COMPLETED"
            ? (question.questionState as ChatQuestionState.CompletedDTO)
                .answerJson
            : question.questionState.case === "ACTIVE"
              ? ((question.questionState as ChatQuestionState.ActiveDTO)
                  .draftAnswerJson ?? undefined)
              : undefined;
        const answerJson = pendingAnswers[questionId] ?? null;
        return {
          body: (
            <ChatOpenEndedQuestionBody
              key={`${questionId}:${timelineRevision}`}
              questionDetailsJson={
                question.respondentQuestionDetails.questionDetailsJSON
              }
              draftAnswerJson={draftAnswerJson}
              fontFamily={fontFamily}
              fontScale={fontScale}
              isMandatory={question.respondentQuestionDetails.isMandatory}
              onAnswerChange={(nextAnswerJson, isValid): void => {
                setPendingAnswer(questionId, nextAnswerJson, isValid);
              }}
            />
          ),
          completedAnswer: ((): React.ReactElement | null => {
            if (question.questionState.case !== "COMPLETED") return null;
            const completedAnswerJson = (
              question.questionState as ChatQuestionState.CompletedDTO
            ).answerJson;
            return (
              <ChatItemContainer
                alignment="outgoing"
                ariaLabel={t("surveys.chatInterface.surveyAnswerAriaLabel")}
              >
                {completedAnswerJson === EMPTY_JSON ? (
                  <Typography sx={typography.b1}>
                    {skippedAnswerText}
                  </Typography>
                ) : (
                  <ChatOpenEndedAnswer
                    questionDetailsJson={
                      question.respondentQuestionDetails.questionDetailsJSON
                    }
                    answerJson={completedAnswerJson}
                    showMoreText={openEndedShowMoreText}
                    showLessText={openEndedShowLessText}
                    skippedAnswerText={skippedAnswerText}
                  />
                )}
              </ChatItemContainer>
            );
          })(),
          primaryAction: isCurrentQuestion
            ? {
                label: nextButtonText,
                name: "sendChatSurveyResponse",
                onClick: (): void => {
                  submitAnswer(questionId, answerJson ?? EMPTY_JSON);
                },
                disabled:
                  areActionsDisabled ||
                  pendingAnswerValidity[questionId] === false ||
                  (question.respondentQuestionDetails.isMandatory &&
                    answerJson === null),
              }
            : undefined,
          bodyScrollMode: "always",
        };
      }
      case QuestionType.QuestionType.MULTIPLE_CHOICE: {
        const questionId = question.respondentQuestionDetails.questionId;
        const draftAnswerJson =
          isEditing && question.questionState.case === "COMPLETED"
            ? (question.questionState as ChatQuestionState.CompletedDTO)
                .answerJson
            : question.questionState.case === "ACTIVE"
              ? ((question.questionState as ChatQuestionState.ActiveDTO)
                  .draftAnswerJson ?? undefined)
              : undefined;
        const answerJson = pendingAnswers[questionId] ?? null;
        return {
          body: (
            <ChatMultiSelectQuestionBody
              key={`${questionId}:${timelineRevision}`}
              questionDetailsJson={
                question.respondentQuestionDetails.questionDetailsJSON
              }
              draftAnswerJson={draftAnswerJson}
              fontFamily={fontFamily}
              fontScale={fontScale}
              maxSelectionText={multiSelectMaxSelectionText}
              isMandatory={question.respondentQuestionDetails.isMandatory}
              onAnswerChange={(nextAnswerJson, isValid): void => {
                setPendingAnswer(questionId, nextAnswerJson, isValid);
              }}
            />
          ),
          completedAnswer: ((): React.ReactElement | null => {
            if (question.questionState.case !== "COMPLETED") return null;
            const completedAnswerJson = (
              question.questionState as ChatQuestionState.CompletedDTO
            ).answerJson;
            return (
              <ChatItemContainer
                alignment="outgoing"
                ariaLabel={t("surveys.chatInterface.surveyAnswerAriaLabel")}
              >
                {completedAnswerJson === EMPTY_JSON ? (
                  <Typography sx={typography.b1}>
                    {skippedAnswerText}
                  </Typography>
                ) : (
                  <ChatMultiSelectAnswerList
                    questionDetailsJson={
                      question.respondentQuestionDetails.questionDetailsJSON
                    }
                    answerJson={completedAnswerJson}
                  />
                )}
              </ChatItemContainer>
            );
          })(),
          primaryAction: isCurrentQuestion
            ? {
                label: nextButtonText,
                name: "sendChatSurveyResponse",
                onClick: (): void => {
                  submitAnswer(questionId, answerJson ?? EMPTY_JSON);
                },
                disabled:
                  areActionsDisabled ||
                  pendingAnswerValidity[questionId] === false ||
                  (question.respondentQuestionDetails.isMandatory &&
                    answerJson === null),
              }
            : undefined,
        };
      }
      case QuestionType.QuestionType.SINGLE_CHOICE: {
        const questionId = question.respondentQuestionDetails.questionId;
        const draftAnswerJson =
          isEditing && question.questionState.case === "COMPLETED"
            ? (question.questionState as ChatQuestionState.CompletedDTO)
                .answerJson
            : question.questionState.case === "ACTIVE"
              ? ((question.questionState as ChatQuestionState.ActiveDTO)
                  .draftAnswerJson ?? undefined)
              : undefined;
        const answerJson = pendingAnswers[questionId] ?? null;
        return {
          body: (
            <ChatSingleSelectQuestionBody
              key={`${questionId}:${timelineRevision}`}
              questionDetailsJson={
                question.respondentQuestionDetails.questionDetailsJSON
              }
              draftAnswerJson={draftAnswerJson}
              fontFamily={fontFamily}
              fontScale={fontScale}
              dropdownPlaceholder={dropdownPlaceholder}
              onAnswerChange={(nextAnswerJson): void => {
                setPendingAnswer(questionId, nextAnswerJson);
              }}
            />
          ),
          completedAnswer: ((): React.ReactElement | null => {
            if (question.questionState.case !== "COMPLETED") return null;
            const completedAnswerJson = (
              question.questionState as ChatQuestionState.CompletedDTO
            ).answerJson;
            return (
              <ChatItemContainer
                alignment="outgoing"
                ariaLabel={t("surveys.chatInterface.surveyAnswerAriaLabel")}
              >
                <Typography
                  sx={{
                    ...typography.b1,
                  }}
                >
                  {completedAnswerJson === EMPTY_JSON ? (
                    skippedAnswerText
                  ) : (
                    <ChatSingleSelectAnswerText
                      questionDetailsJson={
                        question.respondentQuestionDetails.questionDetailsJSON
                      }
                      answerJson={completedAnswerJson}
                    />
                  )}
                </Typography>
              </ChatItemContainer>
            );
          })(),
          primaryAction: isCurrentQuestion
            ? {
                label: nextButtonText,
                name: "sendChatSurveyResponse",
                onClick: (): void => {
                  submitAnswer(questionId, answerJson ?? EMPTY_JSON);
                },
                disabled:
                  areActionsDisabled ||
                  (question.respondentQuestionDetails.isMandatory &&
                    answerJson === null),
              }
            : undefined,
        };
      }
    }
  };

  const renderElement = (
    element: ChatSurveyElement.ChatSurveyElementDTO,
  ): React.ReactElement => {
    switch (element.case) {
      case "WELCOME": {
        const welcome = element as ChatSurveyElement.WelcomeDTO;
        const startAction: ChatItemAction | undefined =
          nextAction?.case === "START_SURVEY"
            ? {
                label: startButtonText,
                name: "startChatSurvey",
                onClick: onStartSurvey,
                disabled: areActionsDisabled,
              }
            : undefined;

        return (
          <ChatItemContainer
            ariaLabel={t("surveys.chatInterface.welcomeMessageAriaLabel")}
            primaryAction={startAction}
          >
            <ChatMessageRichText
              name="chatWelcomeMessage"
              text={welcome.welcomePageText.text}
              typography={regularTypography}
              attachmentConfig={attachmentConfig}
            />
          </ChatItemContainer>
        );
      }
      case "QUESTION": {
        const question = element as ChatSurveyElement.QuestionDTO;
        const isEditing =
          editingQuestionId === question.respondentQuestionDetails.questionId;
        const isActive = question.questionState.case === "ACTIVE" || isEditing;
        const questionType = QuestionType.fromDTO(
          question.respondentQuestionDetails.questionType,
        );
        const isCurrentQuestion =
          isEditing ||
          (nextAction?.case === "COMPLETE_QUESTION" &&
            (nextAction as ChatSurveyNextAction.CompleteQuestionDTO)
              .questionId === question.respondentQuestionDetails.questionId);
        const presentation = getQuestionPresentation(
          question,
          questionType,
          isCurrentQuestion,
          isEditing,
        );

        const questionText = question.respondentQuestionDetails.question?.text;
        const questionDescription =
          question.respondentQuestionDetails.questionDescription?.text;

        return (
          <Stack gap={spacing.spaceSM}>
            <ChatItemContainer
              ariaLabel={t("surveys.chatInterface.surveyQuestionAriaLabel")}
              title={
                questionText ? (
                  <Typography
                    component="h2"
                    sx={{
                      ...typography.sh2,
                      color: isActive ? tokens.label : tokens.labelSubtle,
                      margin: 0,
                    }}
                  >
                    {questionText}
                  </Typography>
                ) : undefined
              }
              description={
                questionDescription ? (
                  <Typography
                    component="p"
                    sx={{
                      ...typography.b2,
                      color: tokens.labelSubtle,
                      margin: 0,
                    }}
                  >
                    {questionDescription}
                  </Typography>
                ) : undefined
              }
              actions={
                isEditing &&
                questionType !== QuestionType.QuestionType.GROUP &&
                questionType !== QuestionType.QuestionType.GRID
                  ? [
                      {
                        label: cancelEditAnswerButtonText,
                        name: "cancelEditChatSurveyAnswer",
                        onClick: cancelEditing,
                        disabled: areActionsDisabled,
                      },
                      ...(questionType === QuestionType.QuestionType.RANKING &&
                      presentation.secondaryAction
                        ? [presentation.secondaryAction]
                        : []),
                      ...(presentation.primaryAction
                        ? [presentation.primaryAction]
                        : []),
                    ]
                  : undefined
              }
              primaryAction={isEditing ? undefined : presentation.primaryAction}
              secondaryAction={
                isEditing ? undefined : presentation.secondaryAction
              }
              bodyScrollMode={presentation.bodyScrollMode}
              flushBodyBottom={
                isActive &&
                (questionType === QuestionType.QuestionType.GROUP ||
                  questionType === QuestionType.QuestionType.GRID)
              }
            >
              {isActive && presentation.body}
            </ChatItemContainer>

            {!isActive && presentation.completedAnswer && (
              <Stack gap={spacing.spaceXS}>
                {presentation.completedAnswer}
                {nextAction?.case !== "SURVEY_COMPLETED" &&
                  questionType !== QuestionType.QuestionType.GRID &&
                  questionType !== QuestionType.QuestionType.GROUP &&
                  questionType !== QuestionType.QuestionType.MESSAGE && (
                    <Stack alignItems="flex-end">
                      <Box
                        component="button"
                        type="button"
                        name="editChatSurveyAnswer"
                        disabled={areActionsDisabled}
                        onClick={(): void =>
                          beginEditing(
                            question.respondentQuestionDetails.questionId,
                          )
                        }
                        sx={{
                          ...typography.b2,
                          fontWeight: 600,
                          color: tokens.backgroundInverse,
                          background: "transparent",
                          border: 0,
                          cursor: areActionsDisabled ? "default" : "pointer",
                          padding: `${spacing.spaceXS} ${spacing.spaceSM}`,
                          "&:hover": {
                            background: "transparent",
                            textDecoration: "underline",
                          },
                        }}
                      >
                        {questionType === QuestionType.QuestionType.RANKING
                          ? editAnswersButtonText
                          : editAnswerButtonText}
                      </Box>
                    </Stack>
                  )}
              </Stack>
            )}
          </Stack>
        );
      }
      case "THANK_YOU": {
        const thankYou = element as ChatSurveyElement.ThankYouDTO;
        return (
          <ChatItemContainer
            ariaLabel={t("surveys.chatInterface.thankYouMessageAriaLabel")}
          >
            <ChatMessageRichText
              name="chatThankYouMessage"
              text={thankYou.thankYouPageText.text}
              typography={regularTypography}
              attachmentConfig={attachmentConfig}
            />
          </ChatItemContainer>
        );
      }
      default:
        throw new Error(`Unsupported chat survey element: ${element.case}`);
    }
  };

  return (
    <Stack
      component="ol"
      ref={timelineContentRef}
      aria-live="polite"
      minHeight="100%"
      width="100%"
      alignSelf="center"
      justifyContent="flex-end"
      gap={spacing.spaceLG}
      paddingBlock={CHAT_TIMELINE_VERTICAL_GUTTER}
      sx={{ listStyle: "none", margin: 0, paddingInlineStart: 0 }}
    >
      {(editingQuestionId === null
        ? elements
        : elements.slice(
            0,
            elements.findIndex(
              (element) =>
                element.case === "QUESTION" &&
                (element as ChatSurveyElement.QuestionDTO)
                  .respondentQuestionDetails.questionId === editingQuestionId,
            ) + 1,
          )
      ).map((element) => (
        <Box
          component="li"
          key={getElementKey(element)}
          ref={
            element.case === "QUESTION" &&
            (element as ChatSurveyElement.QuestionDTO).respondentQuestionDetails
              .questionId === editingQuestionId
              ? editingQuestionRef
              : undefined
          }
        >
          {renderElement(element)}
        </Box>
      ))}
      {submitRetryPrompt && (
        <Box component="li">
          <Stack
            alignItems="center"
            gap={spacing.spaceMD}
            paddingBlock={spacing.spaceLG}
            textAlign="center"
            width="100%"
          >
            <Typography sx={{ ...typography.b2, color: tokens.label }}>
              {submitRetryPrompt.description}
            </Typography>
            <Button
              label={submitRetryPrompt.buttonText}
              name="reopenSubmitConfirmation"
              variant="filled"
              color="primary"
              size="medium"
              disabled={submitRetryPrompt.disabled}
              onClick={submitRetryPrompt.onClick}
            />
          </Stack>
        </Box>
      )}
      <Box
        component="li"
        ref={endOfTimelineRef}
        aria-hidden="true"
        height={0}
      />
    </Stack>
  );
};
